import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import GetAppIcon from '@mui/icons-material/GetApp';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { Box, ListItemIcon, ListItemText, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useRef } from 'react';
import {
  type SecureGlobalDownloadQueryData,
  useSecureGlobalDownload,
} from '@/components/DataGenerationHistory/hooks/useSecureGlobalDownload';
import { openDownloadUrl } from '@/components/Downloads/helpers';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { type DownloadHistory, UserDownloadStatusEnum } from '@/types/GraphqlSchema';
import { formatDate } from '@/utils/helpers';
import { styles as dataGenerationHistoryStyles } from './styles';

interface DataGenerationHistoryItemProps {
  download: DownloadHistory;
}

const DOWNLOAD_ERROR_MESSAGE = 'Unable to start the download. Please try again.';

const getStatusIcon = (status: UserDownloadStatusEnum) => {
  if (status === UserDownloadStatusEnum.Completed) return <CheckCircleIcon color='success' />;
  if (status === UserDownloadStatusEnum.Failed) return <ErrorIcon color='error' />;
  if (status === UserDownloadStatusEnum.InProgress)
    return <CircularProgress size={20} color='warning' />;
  if (status === UserDownloadStatusEnum.Requested) return <HourglassTopIcon color='info' />;
  return <CircularProgress size={20} color='inherit' />;
};

export const DataGenerationHistoryItem = ({ download }: DataGenerationHistoryItemProps) => {
  const { setSnack } = useSnackBarContext();
  const previousDataRef = useRef<SecureGlobalDownloadQueryData | null>(null);
  const { downloadDocument, data, error } = useSecureGlobalDownload(download.id);

  useEffect(() => {
    if (!data || data === previousDataRef.current) return;

    previousDataRef.current = data;

    const secureDownload = data.secureGlobalDownload;
    const url = secureDownload?.url;
    const filename = secureDownload?.filename;

    if (!url || !filename) {
      setSnack({
        open: true,
        message: DOWNLOAD_ERROR_MESSAGE,
        severity: 'error',
      });
      return;
    }

    const downloadMessage = secureDownload.downloadMessage ?? '';
    setSnack({
      open: true,
      message: `Your download should begin shortly.${downloadMessage}`,
      severity: 'success',
    });
    openDownloadUrl(url, filename);
  }, [data, setSnack]);

  useEffect(() => {
    if (!error) return;

    setSnack({
      severity: 'error',
      message: error.message,
      open: true,
    });
  }, [error, setSnack]);

  return (
    <Box sx={dataGenerationHistoryStyles.drawerItem}>
      <ListItemIcon sx={dataGenerationHistoryStyles.listItemIcon}>
        {getStatusIcon(download.status)}
      </ListItemIcon>
      <ListItemText primary={download.filename} secondary={formatDate(download.requestedAt)} />
      {download.status === 'completed' && download.presignedUrl && (
        <Button
          sx={dataGenerationHistoryStyles.button}
          variant='contained'
          onClick={downloadDocument}
          aria-label={`Download ${download.filename}`}
        >
          <GetAppIcon fontSize='small' color='primary' />
        </Button>
      )}
      {download.status === 'in_progress' && (
        <Box display='flex' alignItems='center'>
          <Typography variant='caption' color='textSecondary' sx={{ ml: 1 }}>
            Generating...
          </Typography>
        </Box>
      )}
      {download.status === 'failed' && (
        <Typography variant='caption' color='error'>
          Failed to generate
        </Typography>
      )}
    </Box>
  );
};
